import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Panel } from 'react-bootstrap';
import _ from 'lodash';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';
import Permission from '../../components/Permission/Permission';

import { useAlertStore } from '../../store/providers/Alert';
import { useFaqsStore } from '../../store/providers/Faqs';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';

import Spinner from '../../components/Spinner/Spinner';

import * as constants from '../../constants';
import { columns, sorting } from './FaqsSchema';

const BaseList = React.lazy(() => import('../../components/Lists/BaseList/BaseList'));
const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));
const FaqModal = React.lazy(() => import('../../components/Modals/FaqModal/FaqModal'));

function Faq() {
    const dispatch = useDispatch();

    const { addAlert } = useAlertStore();
    const { selectRecord, listRecords, newRecord, deleteRecord, data, clearData } = useFaqsStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [listData, setListData] = useState([]);

    const pagePermission = constants.PERMISSION_CONTENT_FAQQUESTIONS;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const selectedHandler = (faq) => {
        setIsEditing(true);
        selectRecord({ faqId: faq.id });
    };

    const addedHandler = () => {
        setIsEditing(true);
        newRecord();
    };

    const refreshedHandler = () => {
        listRecords();
    };

    const deletedHandler = (faq) => {
        deleteRecord({ faqId: faq.id });
    };

    const cancelledHandler = () => {
        setIsEditing(false);
    };

    const submittedHandler = () => {
        setIsEditing(false);
        listRecords();
    };

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') {
                listRecords();
            }

            if (data.status === 'success' && data.scope === 'list') {
                setListData(data.body);
            }

            setIsBusy(data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list'));
        }
    }, [data, addAlert, clearData, listRecords]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'faqQuestions', parent: 'Content', text: 'FAQ Questions', path: window.location.pathname }));
        listRecords();
        setIsMounted(true);
    }, [isMounted, hasPermission, listRecords, dispatch]);

    return (
        <>
            <SideMenu activeKey="content" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit}/>
                { hasPermission && isMounted &&
                    <Suspense fallback={<Spinner text="Loading..." />}>
                        {
                            isEditing && <FaqModal
                                show={isEditing}
                                canEdit={canEdit}
                                title={(!_.isEmpty(data) && !_.isEmpty(data.body)) ? 'Edit FAQ Question' : 'Add FAQ Question'}
                                cancelled={() => cancelledHandler()}
                                submitted={() => submittedHandler()} />
                        }
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="FAQ Questions" />
                            <Panel>
                                <Panel.Body>
                                    <BaseList
                                        compType="FAQ Question"
                                        listData={listData}
                                        isBusy={isBusy}
                                        sorting={sorting}
                                        columns={columns}
                                        hideCopy={true}
                                        hidePrint={true}
                                        hideFilter={true}
                                        permissionEdit={canEdit}
                                        added={() => addedHandler()}
                                        selected={(faq) => selectedHandler(faq)}
                                        deleted={(faq) => deletedHandler(faq)}
                                        refreshed={() => refreshedHandler()} />
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(Faq));
